import CropFileUploadButton from '@components/common/CropFileUploadButton';
import DeeplinkActionEdit from '@components/feed/DeepLinkActionEdit';
import * as api from '@apis/calendar-events';
import { CalendarEvent } from '@models/calendar-event';
import { DeepLinkActions, DeeplinkActionPayload } from '@models/common';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { cdnImageUrl } from '@utils/image';
import { extractFileName } from '@utils/string';
import { useMutation } from '@tanstack/react-query';
import { Button, Checkbox, DatePicker, Form, Input, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

const { RangePicker } = DatePicker;

interface CalendarEventFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialData?: CalendarEvent;
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  location: string;
  title: string;
  dateRange: [Dayjs, Dayjs];
  priority: boolean;
};

export const CalendarEventFormModal: React.FC<CalendarEventFormModalProps> = ({
  open,
  mode,
  initialData,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<FormValues>();
  const dialog = useOkCancelDialog();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageUrlTemp, setImageUrlTemp] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [action, setAction] = useState<DeepLinkActions | undefined>();
  const [payload, setPayload] = useState<DeeplinkActionPayload | undefined>();

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && initialData) {
      form.setFieldsValue({
        location: initialData.location,
        title: initialData.title,
        dateRange: [dayjs(initialData.startDate), dayjs(initialData.endDate)],
        priority: initialData.priority ?? false,
      });
      setImageUrl(initialData.imageUrl || '');
      setImageUrlTemp('');
      setImageFile(null);
      setAction(initialData.action);
      setPayload(initialData.payload);
    } else {
      form.resetFields();
      setImageUrl('');
      setImageUrlTemp('');
      setImageFile(null);
      setAction(undefined);
      setPayload(undefined);
    }
  }, [open, mode, initialData, form]);

  const createMutation = useMutation({
    mutationFn: api.createCalendarEvent,
    onSuccess: () => {
      dialog.open({ type: 'ok', title: '알림', content: '행사를 추가했습니다.' });
      onSuccess();
      onClose();
    },
    onError: (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) => api.updateCalendarEvent(id, formData),
    onSuccess: () => {
      dialog.open({ type: 'ok', title: '알림', content: '행사를 수정했습니다.' });
      onSuccess();
      onClose();
    },
    onError: (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  const handleImageChange = (files: FileList) => {
    if (!files?.[0]) return;
    const file = files[0];
    const { fileName, fileExt } = extractFileName(file.name);
    const uploadFileName = `${fileName}.${fileExt || file.type.split('/')[1] || 'jpg'}`;
    const renamed = new File([file], uploadFileName, { type: file.type });
    setImageFile(renamed);
    setImageUrl(uploadFileName);
    setImageUrlTemp(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!imageUrl && !imageUrlTemp) {
        dialog.open({ type: 'ok', title: '알림', content: '정방형 썸네일을 등록해 주세요.' });
        return;
      }
      if (!action) {
        dialog.open({ type: 'ok', title: '알림', content: '클릭시 이동 설정을 선택해 주세요.' });
        return;
      }

      const [start, end] = values.dateRange;
      const submitPayload = {
        location: values.location,
        title: values.title,
        action,
        payload: payload || {},
        startDate: moment(start.toDate()).startOf('day').toDate(),
        endDate: moment(end.toDate()).endOf('day').toDate(),
        imageUrl,
        priority: values.priority ?? false,
      };

      const formData = new FormData();
      formData.append('json', JSON.stringify(submitPayload));
      if (imageFile) {
        formData.append('files', imageFile);
      }

      if (mode === 'create') {
        await createMutation.mutateAsync(formData);
      } else if (initialData?._id) {
        await updateMutation.mutateAsync({ id: initialData._id, formData });
      }
    } catch {
      // validation error
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={mode === 'create' ? '행사 추가' : '행사 수정'}
      open={open}
      onCancel={onClose}
      width={560}
      destroyOnClose
      footer={
        <div>
          <Button type="primary" loading={isPending} onClick={handleSubmit}>
            저장
          </Button>
          <Button onClick={onClose}>취소</Button>
        </div>
      }>
      <Form form={form} layout="vertical">
        <Form.Item name="location" label="장소" rules={[{ required: true, message: '장소를 입력해 주세요.' }]}>
          <Input placeholder="예: 서울숲" />
        </Form.Item>
        <Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력해 주세요.' }]}>
          <Input placeholder="행사 제목" />
        </Form.Item>
        <Form.Item name="priority" label="노출 우선순위 설정" valuePropName="checked" initialValue={false}>
          <Checkbox>우선순위</Checkbox>
        </Form.Item>
        <Form.Item name="dateRange" label="행사 기간" rules={[{ required: true, message: '날짜를 선택해 주세요.' }]}>
          <RangePicker style={{ width: '100%' }} allowEmpty={[false, false]} />
        </Form.Item>
        <Form.Item label="정방형 썸네일" required>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            {(imageUrlTemp || imageUrl) && (
              <img
                src={cdnImageUrl(imageUrlTemp || imageUrl)}
                alt="thumbnail"
                style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
              />
            )}
            <CropFileUploadButton
              accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg'].map((ext) => '.' + ext).join(',')}
              className="link-button"
              style={{
                display: 'flex',
                justifyContent: 'center',
                height: 36,
                width: 200,
                border: '1px solid #aaaaaa',
                borderRadius: 8,
                lineHeight: '36px',
                cursor: 'pointer',
              }}
              onChangeFiles={handleImageChange}>
              썸네일 {imageUrl || imageUrlTemp ? '변경' : '등록'}
            </CropFileUploadButton>
          </div>
        </Form.Item>
        <Form.Item label="클릭시 이동 설정" required>
          <div style={{ border: '1px solid #eaeaea', borderRadius: 8, padding: 8 }}>
            <DeeplinkActionEdit
              data={{ action, payload }}
              onChange={(value) => {
                setAction(value.action);
                setPayload(value.payload);
              }}
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CalendarEventFormModal;
